import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { products, Product } from '../data/products';
import './ProductDetailPage.css';
import { ProductCard } from '../components';
import { useCategoryById } from '../hooks/useCategoryById';
import { fetchAllProducts, ApiProduct } from '../services/api';

const ProductDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string; category?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { translations, currentLanguage, isRTL } = useLanguage();
    const [product, setProduct] = useState<Product | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
    
    // Get category ID from product's category field
    const categoryId = product?.category ? parseInt(product.category) : null;
    const { category: categoryData } = useCategoryById(categoryId);

    // Transform API product to local Product format
    const transformApiProduct = (apiProduct: ApiProduct): Product => {
        return {
            id: apiProduct.id,
            title: apiProduct.name.en,
            titleAr: apiProduct.name.ar,
            description: apiProduct.description.en,
            descriptionAr: apiProduct.description.ar,
            category: apiProduct.category,
            subcategory: apiProduct.subcategory,
            image: apiProduct.images[0] || '',
            images: apiProduct.images,
            price: apiProduct.price,
        };
    };

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            
            // First, try to get product from navigation state
            const stateProduct = location.state?.product as Product | undefined;
            
            if (stateProduct && stateProduct.id === parseInt(id)) {
                // Use product from navigation state
                console.log('Using product from navigation state:', stateProduct);
                setProduct(stateProduct);
                setCurrentImageIndex(0);
                setIsLoading(false);
            } else {
                // Fallback: Find the product from local data
                console.log('Product not in state, searching locally...');
                const foundProduct = products.find(p => p.id === parseInt(id));
                setProduct(foundProduct || null);
                setCurrentImageIndex(0);
                setIsLoading(false);
            }
        }
    }, [id, location.state]);

    // Fetch related products from API
    useEffect(() => {
        const loadRelatedProducts = async () => {
            if (!product) return;

            try {
                console.log('Fetching related products for product:', product.id);
                const apiProducts = await fetchAllProducts();
                
                // Filter: exclude current product, only active products, limit to 4
                const transformedProducts = apiProducts
                    .filter(p => p.isActive && p.id !== product.id)
                    .map(transformApiProduct)
                    .slice(0, 4);
                
                console.log('Related products loaded:', transformedProducts.length);
                setRelatedProducts(transformedProducts);
            } catch (error) {
                console.error('Error loading related products:', error);
                setRelatedProducts([]);
            }
        };

        loadRelatedProducts();
    }, [product?.id, product?.category, product]);

    // Get product images array or fallback to single image
    const productImages = product?.images && product.images.length > 0 ? product.images : [product?.image].filter(Boolean);

    const nextImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
    }, [productImages.length]);

    const prevImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
    }, [productImages.length]);

    const goToImage = (index: number) => {
        setCurrentImageIndex(index);
    };

    // Keyboard navigation for image gallery
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (productImages.length > 1) {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    prevImage();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    nextImage();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [productImages.length, nextImage, prevImage]);

    if (isLoading) {
        return (
            <div className="product-detail-page">
                <div className="container">
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <p>{translations.loading || 'Loading...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="product-detail-page">
                <div className="container">
                    <div className="product-not-found">
                        <h1>
                            {isRTL ? 'المنتج غير موجود' : 'Product Not Found'}
                        </h1>
                        <p>
                            {isRTL
                                ? `عذراً، المنتج رقم ${id} غير متوفر.`
                                : `Sorry, product with ID ${id} is not available.`
                            }
                        </p>
                        <Link to="/products" className="btn btn-primary">
                            {translations.backToProducts || 'Back to Products'}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const title = currentLanguage.code === 'ar' ? product.titleAr : product.title;
    const description = currentLanguage.code === 'ar' ? product.descriptionAr : product.description;
    const categoryName = categoryData 
        ? (isRTL ? categoryData.name.ar : categoryData.name.en)
        : (product.category);

    return (
        <div className={`product-detail-page ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="container">
                {/* Breadcrumb */}
                <nav className="breadcrumb">
                    <Link to="/" className="breadcrumb-link">
                        {translations.home}
                    </Link>
                    <span className="breadcrumb-separator">/</span>
                    <Link to={`/products/${product.category}`} className="breadcrumb-link">
                        {categoryName}
                    </Link>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">{title}</span>
                </nav>

                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="back-button"
                >
                    <ArrowLeft size={20} className={isRTL ? 'flip' : ''} />
                    {translations.backToProducts}
                </button>

                {/* Product Detail */}
                <div className="product-detail">
                    <div className="product-image-section">
                        <div className="product-image-container">
                            <div className="main-image-wrapper">
                                <img
                                    src={`https://api.dawaak-andna.com${productImages[currentImageIndex]}`}
                                    alt={title}
                                    className="product-image"
                                />

                                {productImages.length > 1 && (
                                    <>
                                        <button
                                            className={`image-nav-btn prev-btn ${isRTL ? 'rtl' : ''}`}
                                            onClick={prevImage}
                                            aria-label={isRTL ? 'الصورة السابقة' : 'Previous image'}
                                        >
                                            <ChevronLeft size={20} />
                                        </button>
                                        <button
                                            className={`image-nav-btn next-btn ${isRTL ? 'rtl' : ''}`}
                                            onClick={nextImage}
                                            aria-label={isRTL ? 'الصورة التالية' : 'Next image'}
                                        >
                                            <ChevronRight size={20} />
                                        </button>
                                    </>
                                )}
                            </div>
                            {productImages.length > 1 && (
                                <div className="image-thumbnails">
                                    {productImages.map((image, index) => (
                                        <button
                                            key={index}
                                            className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => goToImage(index)}
                                        >
                                            <img src={`https://api.dawaak-andna.com${image}`} alt={`${title} - ${index + 1}`} />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="product-info-section">
                        <div className="product-category">
                            <Package size={16} />
                            {categoryName}
                        </div>

                        <h1 className="product-title">{title}</h1>

                        <div className="product-price-section">
                            <div className="price-display">
                                <span>{isRTL ? 'السعر:' : 'Price:'}</span>
                                <span className="price-amount">{product.price}</span>
                                <span className="price-currency">
                                    {isRTL ? 'ريال سعودي' : 'SAR'}
                                </span>
                            </div>
                        </div>

                        <div className="product-description">
                            <h3>{translations.productDetails}</h3>
                            <p>{description}</p>
                        </div>

                        <div className="product-actions">
                            <button 
                                onClick={() => {
                                    const message = `*${isRTL ? 'استفسار عن المنتج' : 'Product Inquiry'}:*\n${title}\n\n${isRTL ? 'أود الاستفسار عن هذا المنتج' : 'I would like to inquire about this product'}`;
                                    const encodedMessage = encodeURIComponent(message);
                                    const whatsappUrl = `https://wa.me/966535339904?text=${encodedMessage}`;
                                    window.open(whatsappUrl, '_blank');
                                }}
                                className="btn btn-primary"
                            >
                                {translations.contactUs}
                            </button>
                            <Link to={`/products/${product.category}`} className="btn btn-outline">
                                {isRTL ? 'منتجات مشابهة' : 'Similar Products'}
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Related Products */}
                <div className="related-products">
                    <h2>
                        {isRTL ? 'منتجات ذات صلة' : 'Related Products'}
                    </h2>

                    {relatedProducts.length > 0 ? (
                        <div className="related-products-grid">
                            {relatedProducts.map(relatedProduct => (
                                <ProductCard
                                    key={relatedProduct.id}
                                    product={relatedProduct}
                                />
                            ))}
                        </div>
                    ) : (
                        <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                            {isRTL ? 'لا توجد منتجات ذات صلة' : 'No related products available'}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductDetailPage;